import { CSSProperties } from 'react';

import { NotificationTimingPreference } from '@/generated';
import { FeatureFlagType } from '@/pages/Guards/WithFeatureAccess';
import theme from '@/theme';
import { Nullable, StringWithAutocompleteOptions } from '@/utils/globalTypes';
import { PERMISSIONS, Permission } from '@/utils/permissionTypes';

import { FeedItem } from './NotificationFeed/NotificationFeedCell';

export enum JobNotificationEvent {
  RunFailed = 'job-run-failed',
}

export const jobNotificationEventDescriptions = {
  [JobNotificationEvent.RunFailed]: 'Job run failed',
} as const satisfies Record<JobNotificationEvent, string>;

export type NotificationEvent =
  | 'campaign-approved'
  | 'campaign-approval-canceled'
  | 'campaign-sending-started'
  | 'campaign-sending-completed'
  | 'campaign-suspended'
  | 'campaign-terminated'
  | 'campaign-resumed'
  | 'campaign-sending-failed'
  | 'campaign-canceled'
  | 'campaign-ready-to-schedule'
  | 'campaign-scheduled'
  | 'campaign-auto-scheduling-disabled'
  | 'campaign-not-approved'
  | 'campaign-unscheduled-warning'
  | 'data-encryption-key-expiration'
  | 'settings-updated';

export const notificationEventsDescriptions = {
  'campaign-approved': 'Campaign has been approved',
  'campaign-approval-canceled': 'Campaign has been unapproved',
  'campaign-sending-started': 'Campaign send has started',
  'campaign-sending-completed': 'Campaign send has completed',
  'campaign-suspended': 'Campaign has been suspended',
  'campaign-terminated': 'Suspended campaign has been terminated',
  'campaign-resumed': 'Suspended campaign has been resumed',
  'campaign-sending-failed': 'Campaign failed to send',
  'campaign-canceled': 'Campaign has been canceled',
  'settings-updated': 'Notification settings have been updated',
  'campaign-ready-to-schedule': 'Campaign is ready to scheduled',
  'campaign-not-approved': 'Campaign has not yet been approved',
  'campaign-unscheduled-warning': 'Upcoming campaign is not yet scheduled',
  'data-encryption-key-expiration': 'Data encryption key is about to expire',
} as const satisfies Partial<Record<NotificationEvent, string>>;

export const DATA_ENCRYPTION_KEY_EXPIRATION_EVENT =
  'data-encryption-key-expiration' as const;

export const timeBasedEvents: ReadonlyArray<
  StringWithAutocompleteOptions<NotificationEvent>
> = ['campaign-not-approved', DATA_ENCRYPTION_KEY_EXPIRATION_EVENT];

export const eventAvailabilityCriteriaMap = new Map<
  StringWithAutocompleteOptions<NotificationEvent>,
  FeatureFlagType
>([[DATA_ENCRYPTION_KEY_EXPIRATION_EVENT, 'hasEncryptionV2']]);

// TODO: Fetch from jobAlertConfigQuery (backend datasource) instead of hardcoding.
// Currently only one metric exists, so hardcoding is pragmatic.
// Once multiple metrics are added to pigeon datasource, migrate to backend-fetched config.
export const ALERT_METRICS = [
  {
    id: 'processed_percentage',
    label: 'Processed rate',
    valueType: 'percentage',
  },
] as const;

export const OPERATOR_DISPLAY: Record<
  string,
  { modal: string; preference: string; title: string }
> = {
  lt: { modal: 'Is less than', preference: 'is less than', title: 'was below' },
  gt: {
    modal: 'Is greater than',
    preference: 'is greater than',
    title: 'was above',
  },
};

export const generateAlertTitle = (
  metricName: string,
  operatorId: string,
  value: number,
  valueType: string,
  currencySymbol: string,
): string => {
  const operatorLabel = OPERATOR_DISPLAY[operatorId]?.preference ?? operatorId;
  let formattedValue: string;
  switch (valueType) {
    case 'percentage':
      formattedValue = `${value}%`;
      break;
    case 'currency':
      formattedValue = `${currencySymbol}${value}`;
      break;
    default:
      formattedValue = String(value);
  }
  return `${metricName} ${operatorLabel} ${formattedValue}`;
};

// Gets random number between 100 and 250
export const getRandomWidthNumber = () => {
  return Math.floor(Math.random() * (250 - 100 + 1) + 100);
};
export type NotificationTimingPreferenceType = {
  category: string;
  event: NotificationEvent;
  timingPreference: Nullable<NotificationTimingPreference>;
};

type FeedItemMarkdownBlock = Extract<
  FeedItem['blocks'][number],
  { type: 'markdown' }
>;

export const getMarkdownBlock = (blocks: FeedItem['blocks']) => {
  return blocks.find(
    (block): block is FeedItemMarkdownBlock => block.type === 'markdown',
  );
};

export const notificationCircleStyling = ({
  isCurrentPage,
  isVisible,
}: Record<'isCurrentPage' | 'isVisible', boolean>): CSSProperties => ({
  position: 'absolute',
  bottom: '12px',
  left: '14px',
  width: '9px',
  height: '9px',
  borderRadius: '50%',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: isCurrentPage
    ? theme.primaryNav.activeBackgrounColor
    : theme.primaryNav.backgroundColor,
  backgroundColor: theme.palette.cyan500,
  color: 'neutral00',
  visibility: isVisible ? 'visible' : 'hidden',
  transitionDuration: '400ms',
  transitionTimingFunction: theme.transitions.easing.easeInOut,
  transitionProperty: 'visibility, width, height',
});

/**
 * Permission-based notification configuration.
 * Maps permissions to notification categories and their default settings.
 */
export type NotificationCategory =
  | 'data'
  | 'analytics'
  | 'campaigns'
  | 'settings';

export interface NotificationPermissionConfig {
  permission: Permission;
  category: NotificationCategory;
  events: ReadonlyArray<StringWithAutocompleteOptions<NotificationEvent>>;
}

/**
 * Maps notification events to their required permission.
 * Used to determine if a user can view/edit notification preferences.
 */
export const NOTIFICATION_PERMISSION_MAP: ReadonlyArray<NotificationPermissionConfig> =
  [
    {
      permission: PERMISSIONS.DATA_READ,
      category: 'data',
      events: ['job-run-failed'],
    },
    {
      permission: PERMISSIONS.SETTINGS_UPDATE,
      category: 'settings',
      events: [DATA_ENCRYPTION_KEY_EXPIRATION_EVENT],
    },
  ] as const;

/**
 * Get the required permission for a specific notification event.
 * Checks explicit mappings first (DATA_READ for data events, SETTINGS_UPDATE for settings events).
 * All unmapped events default to CAMPAIGNS_READ - add new events to NOTIFICATION_PERMISSION_MAP
 * if they require different permissions.
 */
export const getPermissionForEvent = (
  event: StringWithAutocompleteOptions<NotificationEvent>,
): Permission => {
  const mapping = NOTIFICATION_PERMISSION_MAP.find((config) =>
    config.events.includes(event),
  );

  return mapping?.permission ?? PERMISSIONS.CAMPAIGNS_READ;
};
